/**
 * Shared renderer helpers consumed by every per-chart-type builder.
 *
 * Per ADR-0079 §4.4: the renderer is the single place that constructs
 * ECharts function-shaped surfaces (tooltip.formatter, axisLabel.formatter,
 * etc.). All builders go through these helpers so behavior is consistent
 * across cartesian, pie, gauge, treemap, etc.
 */

import type { EChartsOption } from "echarts";
import type {
  CommonOptions,
  EchartsVizSpecV1,
  ValueFormat,
} from "../echartsSpec.js";
import { buildFormatter } from "../valueFormat.js";
import { valueFormatToLegacy } from "../atlasTooltip.js";

export { buildTooltipFormatter, valueFormatToLegacy } from "../atlasTooltip.js";

// ── Column resolution ──────────────────────────────────────────────────────

/**
 * Returns the index of `name` in `dimensions`, or `null` if absent.
 * Used by builders that need a numeric dimension index for `dataset.encode`.
 */
export function resolveColumnIndex(name: string, dimensions: string[]): number | null {
  const idx = dimensions.indexOf(name);
  return idx >= 0 ? idx : null;
}

/**
 * Walk a spec's encoding and return any column references that are missing
 * from `dimensions`. Empty array means every reference resolves.
 *
 * Mirrors the hub-side `validateEchartsSpecAgainstSchema` semantic check, but
 * runs at render time against the live cache. See ADR-0079 §4.7 (column
 * drift): the renderer NEVER coerces silently and NEVER renders a partial
 * chart with missing series.
 */
export function validateEncodedColumns(
  spec: EchartsVizSpecV1,
  dimensions: string[],
): string[] {
  const available = new Set(dimensions);
  const errors: string[] = [];
  const availableList = dimensions.length > 0 ? dimensions.join(", ") : "(none)";

  function check(path: string, column: string): void {
    if (!available.has(column)) {
      errors.push(
        `Encoded column "${column}" not present in input data (at ${path}). ` +
          `Available: ${availableList}. ` +
          `Re-author the chart to use a current column.`,
      );
    }
  }

  switch (spec.chartType) {
    case "line":
    case "bar":
    case "area":
    case "scatter":
      check("encoding.x.column", spec.encoding.x.column);
      if (spec.encoding.y) {
        spec.encoding.y.forEach((y, i) => check(`encoding.y[${i}].column`, y.column));
      }
      if (spec.encoding.series) {
        check("encoding.series.column", spec.encoding.series.column);
        check("encoding.series.valueColumn", spec.encoding.series.valueColumn);
      }
      break;
    case "pie":
    case "donut":
    case "funnel":
      check("encoding.category.column", spec.encoding.category.column);
      check("encoding.value.column", spec.encoding.value.column);
      break;
    case "gauge":
      check("encoding.value.column", spec.encoding.value.column);
      break;
    case "treemap":
    case "sunburst":
      spec.encoding.hierarchy.forEach((h, i) => check(`encoding.hierarchy[${i}].column`, h.column));
      check("encoding.value.column", spec.encoding.value.column);
      break;
    case "candlestick":
      check("encoding.x.column", spec.encoding.x.column);
      check("encoding.ohlc.open", spec.encoding.ohlc.open);
      check("encoding.ohlc.close", spec.encoding.ohlc.close);
      check("encoding.ohlc.low", spec.encoding.ohlc.low);
      check("encoding.ohlc.high", spec.encoding.ohlc.high);
      break;
    case "boxplot":
      check("encoding.x.column", spec.encoding.x.column);
      check("encoding.summary.min", spec.encoding.summary.min);
      check("encoding.summary.q1", spec.encoding.summary.q1);
      check("encoding.summary.median", spec.encoding.summary.median);
      check("encoding.summary.q3", spec.encoding.summary.q3);
      check("encoding.summary.max", spec.encoding.summary.max);
      break;
    case "radar":
      spec.encoding.indicators.forEach((ind, i) =>
        check(`encoding.indicators[${i}].column`, ind.column),
      );
      if (spec.encoding.series) {
        check("encoding.series.column", spec.encoding.series.column);
      }
      break;
    case "sankey":
      check("encoding.source.column", spec.encoding.source.column);
      check("encoding.target.column", spec.encoding.target.column);
      check("encoding.value.column", spec.encoding.value.column);
      break;
    case "heatmap":
      check("encoding.x.column", spec.encoding.x.column);
      check("encoding.y.column", spec.encoding.y.column);
      check("encoding.value.column", spec.encoding.value.column);
      break;
  }
  return errors;
}

// ── Value formatting ───────────────────────────────────────────────────────

/**
 * Build an axis-label formatter from a `ValueFormat`. Returns `undefined`
 * when no format is requested — ECharts then falls back to its default
 * label rendering (typically `String(value)`).
 *
 * Bridges to the existing `buildFormatter` (currency / percent / compact /
 * date) via `valueFormatToLegacy`, so charts and TanTable cells format
 * identically.
 */
export function buildAxisLabelFormatter(
  format: ValueFormat | undefined,
): ((value: unknown) => string) | undefined {
  if (!format) return undefined;
  const legacy = valueFormatToLegacy(format);
  const fn = buildFormatter(legacy);
  return (value: unknown) => fn(value);
}

// ── Palette resolution ─────────────────────────────────────────────────────

/**
 * Resolve a chart-spec color strategy to an explicit ECharts palette array.
 * Returns `undefined` when the strategy is "atlas-default" — in which case
 * the registered ECharts theme (`ATLAS_CHART_THEME_NAME`) supplies the
 * palette via its built-in cycle.
 */
export function resolvePalette(
  color: CommonOptions["color"] | undefined,
  seriesCount: number,
): string[] | undefined {
  if (!color) return undefined;
  if (color.palette && color.palette.length > 0) return color.palette;
  switch (color.strategy) {
    case "atlas-default":
      return undefined;
    case "categorical":
      return takeOrCycle(CATEGORICAL_PALETTE, seriesCount);
    case "sequential":
      return takeOrInterpolate(SEQUENTIAL_PALETTE, seriesCount);
  }
}

const CATEGORICAL_PALETTE = [
  "#5470c6",
  "#91cc75",
  "#fac858",
  "#ee6666",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
  "#ea7ccc",
];

const SEQUENTIAL_PALETTE = [
  "#deebf7",
  "#c6dbef",
  "#9ecae1",
  "#6baed6",
  "#4292c6",
  "#2171b5",
  "#08519c",
  "#08306b",
];

function takeOrCycle(palette: readonly string[], n: number): string[] {
  if (n <= palette.length) return palette.slice(0, n);
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(palette[i % palette.length]);
  return out;
}

function takeOrInterpolate(palette: readonly string[], n: number): string[] {
  if (n === 0) return [];
  if (n === 1) return [palette[Math.floor(palette.length / 2)]];
  if (n <= palette.length) {
    // Spread evenly across the palette range.
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const idx = Math.round(t * (palette.length - 1));
      out.push(palette[idx]);
    }
    return out;
  }
  return takeOrCycle(palette, n);
}

// ── Empty-data graphic ─────────────────────────────────────────────────────

/**
 * Centered "No data" overlay used by every builder when `rows.length === 0`.
 * Per ADR-0079 §4.4 "Empty data": the renderer never throws on empty input.
 */
export function buildNoDataGraphic(): NonNullable<EChartsOption["graphic"]> {
  return [
    {
      type: "text",
      left: "center",
      top: "middle",
      style: {
        text: "No data",
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
        fill: "#999",
      },
    },
  ] as NonNullable<EChartsOption["graphic"]>;
}

// ── Misc ───────────────────────────────────────────────────────────────────

/**
 * Coerce a row cell into a finite number, or `null` if it isn't.
 */
export function coerceNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
